import React from "react";
import {
  getReportingMonth,
  parseReportingMonth,
  type ReportingMonth,
} from "../lib/reportingMonth";

type ReportingMonthBannerProps = {
  reportingMonth: ReportingMonth;
  onReportingMonthChange: (month: ReportingMonth) => void;
};

/** Build list of selectable months from มกราคม 2569 (Jan 2026) forward to current month. */
function getSelectableMonths(): ReportingMonth[] {
  const anchorYear = 2026;
  const anchorMonth = 0; // January (0-indexed)
  const nowInReportingMonth = parseReportingMonth(getReportingMonth(new Date()));
  const endYear = nowInReportingMonth.getFullYear();
  const endMonth = nowInReportingMonth.getMonth();
  const months: ReportingMonth[] = [];
  for (let y = anchorYear; y <= endYear; y++) {
    const startM = y === anchorYear ? anchorMonth : 0;
    const endM = y === endYear ? endMonth : 11;
    for (let m = startM; m <= endM; m++) {
      const d = new Date(y, m, 1);
      months.push(getReportingMonth(d));
    }
  }
  return months.reverse(); // descending: newer months first
}

export function ReportingMonthBanner({ reportingMonth, onReportingMonthChange }: ReportingMonthBannerProps) {
  const label = React.useMemo(() => {
    const date = parseReportingMonth(reportingMonth);
    return new Intl.DateTimeFormat("th-TH-u-ca-buddhist", { month: "long", year: "numeric" }).format(date);
  }, [reportingMonth]);
  const selectableMonths = React.useMemo(() => getSelectableMonths(), []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm space-y-2">
      <div className="text-sm text-slate-600">คุณกำลังกรอกข้อมูลสำหรับ:</div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-lg font-semibold text-slate-900">{label}</div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <span>เปลี่ยนเดือน</span>
          <select
            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm"
            value={reportingMonth}
            onChange={(e) => onReportingMonthChange(e.target.value as ReportingMonth)}
          >
            {selectableMonths.map((month) => {
              const date = parseReportingMonth(month);
              const monthLabel = new Intl.DateTimeFormat("th-TH-u-ca-buddhist", {
                month: "long",
                year: "numeric",
              }).format(date);
              return (
                <option key={month} value={month}>
                  {monthLabel}
                </option>
              );
            })}
          </select>
        </label>
      </div>
    </div>
  );
}
